// src/components/Metas.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { FaTimes, FaGripVertical, FaAlignLeft, FaAlignCenter, FaAlignRight } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faCalendar, faPalette, faPaperPlane, faCopy, faTasks, faTrashCan, faPen } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "./Meta.css";
import "./ListagemEspelho.css";
import "./AtaObjetivos.css";

const CORES_PREDEFINIDAS = [
  "#ffffff", "#fef9c3", "#dcfce7", "#dbeafe", "#fce7f3",
  "#ede9fe", "#ffedd5", "#f0fdf4", "#fdf4ff", "#ecfeff",
];

const gerarAbreviacao = (nome) => {
  if (!nome) return "?";
  if (nome.includes(' ')) return nome.split(' ').filter(p => p.length > 0).map(p => p.charAt(0).toUpperCase()).join('');
  return nome.substring(0, 2).toUpperCase();
};

const podeDesmarcarConclusao = (concluidoEm) => {
  if (!concluidoEm) return true;
  return (new Date() - new Date(concluidoEm)) / (1000 * 60 * 60) < 24;
};

const ehImagem = (a) => (a?.tipo_mime || "").startsWith("image/");

const formatarDataParaExibicao = (dataString) => {
  if (!dataString) return '';
  const [ano, mes, dia] = dataString.split('T')[0].split('-');
  return `${dia}/${mes}/${ano}`;
};

const LinkNotaIcon = ({ onClick, title, temLink }) => (
  <span className="obj-icon-btn" style={{ color: temLink ? '#10b981' : '#9ca3af' }} onClick={onClick} title={title}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  </span>
);

const ChipResponsavel = ({ responsavel, onRemove, disabled }) => {
  const nomeExibicao = responsavel.nome_exibicao || "Usuário";
  const isExterno    = !responsavel.usuario_id;
  const avatarUrl    = responsavel.avatar_url;
  const abreviacao   = gerarAbreviacao(nomeExibicao);

  return (
    <span className={`chip-resp-mini ${isExterno ? 'chip-resp-mini--ext' : ''}`} title={nomeExibicao}>
      {avatarUrl && !isExterno ? (
        <img src={avatarUrl} alt={nomeExibicao} className="chip-resp-mini-img"
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
      ) : null}
      <div className="chip-resp-mini-iniciais" style={{ display: avatarUrl && !isExterno ? 'none' : 'flex' }}>
        {abreviacao}
      </div>
      {!disabled && (
        <span className="chip-resp-mini-remove" onClick={e => { e.stopPropagation(); onRemove(responsavel); }}>×</span>
      )}
      <div className="chip-resp-tooltip">
        <strong>{nomeExibicao}</strong>
        {isExterno && <em>Externo</em>}
      </div>
    </span>
  );
};

const ComentarioIcon = ({ onClick, title, hasComments }) => (
  <span className="obj-icon-btn" style={{ color: hasComments ? '#10b981' : '#9ca3af' }} onClick={onClick} title={title}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  </span>
);

function DescricaoArea({ descricaoData, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft,  setDraft]  = useState(descricaoData?.text || "");
  const [fSize,  setFSize]  = useState(Number(descricaoData?.fontSize) || 14);
  const [fColor, setFColor] = useState(descricaoData?.color || "#374151");
  const [fAlign, setFAlign] = useState(descricaoData?.align || "left");
  const wrapRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setDraft(descricaoData?.text || "");
      setFSize(Number(descricaoData?.fontSize) || 14);
      setFColor(descricaoData?.color || "#374151");
      setFAlign(descricaoData?.align || "left");
    }
  }, [descricaoData, editing]);

  const commit = () => {
    onChange({ text: draft, fontSize: fSize, color: fColor, align: fAlign });
    setEditing(false);
  };

  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) commit();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 50);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handler); };
  }, [editing, draft, fSize, fColor, fAlign]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [editing]);

  if (!editing) {
    const text = descricaoData?.text || "";
    return (
      <div
        className={`metas-descricao-wrapper metas-descricao-display ${!text ? 'metas-descricao-vazia' : ''}`}
        style={{ fontSize: `${fSize}px`, color: fColor, textAlign: fAlign }}
        onClick={() => setEditing(true)}
        title="Clique para editar descrição"
      >
        <span style={{ whiteSpace: 'pre-wrap' }}>{text || "Clique para adicionar uma descrição para esta tabela de metas…"}</span>
        <span className="fb-text-click-hint">✎</span>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="metas-descricao-wrapper metas-descricao-editando">
      <textarea
        ref={textareaRef}
        className="metas-desc-textarea-inline"
        value={draft}
        style={{ fontSize: `${fSize}px`, color: fColor, textAlign: fAlign }}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Escape") { setDraft(descricaoData?.text || ""); setEditing(false); }
          if (e.key === "Enter" && e.ctrlKey) commit();
        }}
        placeholder="Descrição da tabela de metas..."
        rows={3}
      />
      <div className="metas-desc-toolbar">
        <div className="fb-tt-align-group">
          {[
            { v: "left",   Icon: FaAlignLeft },
            { v: "center", Icon: FaAlignCenter },
            { v: "right",  Icon: FaAlignRight },
          ].map(({ v, Icon }) => (
            <button key={v} type="button"
              className={`fb-tt-align-btn${fAlign === v ? " active" : ""}`}
              onMouseDown={e => { e.preventDefault(); setFAlign(v); }}>
              <Icon size={13} />
            </button>
          ))}
        </div>
        <div className="fb-tt-size-group">
          <button type="button" className="fb-tt-sz-btn" onMouseDown={e => { e.preventDefault(); setFSize(s => Math.max(9, s - 1)); }}>−</button>
          <span className="fb-tt-sz-val">{fSize}</span>
          <button type="button" className="fb-tt-sz-btn" onMouseDown={e => { e.preventDefault(); setFSize(s => Math.min(48, s + 1)); }}>+</button>
        </div>
        <label className="fb-tt-color-label" title="Cor do texto">
          <span className="fb-tt-color-swatch" style={{ background: fColor }} />
          <input type="color" className="fb-tt-color-hidden" value={fColor}
            onChange={e => setFColor(e.target.value)} />
        </label>
        <span className="metas-desc-hint">Ctrl+Enter confirma · Esc cancela</span>
        <button type="button" className="metas-desc-confirmar" onMouseDown={e => { e.preventDefault(); commit(); }}>
          Salvar
        </button>
      </div>
    </div>
  );
}

export default function Metas({
  notaId,
  projectId,
  setorId,
  entityType,
  usuarioId,
  projetoNome,
  notaNome,
  onClose,
  containerAtual,
  buscaMetas = "",
  onNotaCopiada,
  onOpenTask,
}) {
  const [metas, setMetas]                               = useState([]);
  const [sugestoesResponsavel, setSugestoesResponsavel] = useState({});
  const [inputResponsavel, setInputResponsavel]         = useState({});
  const [comentariosMeta, setComentariosMeta]           = useState({});
  const [comentarioTemp, setComentarioTemp]             = useState({});
  const [editandoComentario, setEditandoComentario]     = useState({});
  const [enviandoComentario, setEnviandoComentario]     = useState({});
  const [meuNome, setMeuNome]                           = useState("Você");
  const [salvando, setSalvando]                         = useState(false);
  const [novoMetaTexto, setNovoMetaTexto]               = useState("");
  const [editingResponsavelId, setEditingResponsavelId] = useState(null);
  const [colorPickerMetaId, setColorPickerMetaId]       = useState(null);
  const [descricaoData, setDescricaoData]               = useState({ text: "", fontSize: 14, color: "#374151", align: "left" });
  const [salvandoDesc, setSalvandoDesc]                 = useState(false);
  const [filtroResponsavel, setFiltroResponsavel]       = useState(null);

  // ── Estados de filtro/ordenação ──
  const [arquivarConcluidos, setArquivarConcluidos] = useState(false);
  const [soSemResp, setSoSemResp]                   = useState(false);
  const [soEmAberto, setSoEmAberto]                 = useState(false);
  const [ordenacao, setOrdenacao]                   = useState('antigos');
  const ordemDesc  = ordenacao === 'recentes';
  const arrastavel = ordenacao === 'recentes' || ordenacao === 'antigos';

  const [concluidosRecentes, setConcluidosRecentes] = useState({});
  const timeoutsConclusaoRef = useRef({});

  const descTimerRef = useRef(null);
  const cardRef      = useRef(null);
  const [showCopiarPicker, setShowCopiarPicker] = useState(false);
  const [destinosDisponiveis, setDestinosDisponiveis] = useState({ projetos: [], setores: [] });
  const [copiando, setCopiando]                 = useState(false);
  const copiarRef = useRef(null);

  // ── Edição inline de metas ──
  const [editandoMetaId, setEditandoMetaId]     = useState(null);
  const [textoEdicaoMeta, setTextoEdicaoMeta]   = useState("");

  // ── Anexos por meta + modal único (link/tarefa) ──
  const [anexosPorMeta, setAnexosPorMeta]       = useState({});
  const [uploadandoAnexo, setUploadandoAnexo]   = useState({});
  const [imagemAberta, setImagemAberta]         = useState(null);
  const [modalVinculo, setModalVinculo]         = useState(null);
  const [modalView, setModalView]               = useState('entidades');
  const [entidadesContainer, setEntidadesContainer] = useState(null);
  const [entidadeSel, setEntidadeSel]           = useState(null);
  const [itensModal, setItensModal]             = useState([]);
  const [carregandoModal, setCarregandoModal]   = useState(false);
  const [buscaModal, setBuscaModal]             = useState("");

  // ── Cabeçalho da lista: data, comentários e anexos ──
  const [dataEntregaLista, setDataEntregaLista]           = useState("");
  const [comentariosLista, setComentariosLista]           = useState([]);
  const [comentarioListaTemp, setComentarioListaTemp]     = useState("");
  const [showComentariosLista, setShowComentariosLista]   = useState(false);
  const [enviandoComentarioLista, setEnviandoComentarioLista] = useState(false);
  const [anexosLista, setAnexosLista]                     = useState([]);
  const [uploadandoAnexoLista, setUploadandoAnexoLista]   = useState(false);
  const comentariosListaRef = useRef(null);

  // Cleanup dos timeouts de conclusão
  useEffect(() => () => { Object.values(timeoutsConclusaoRef.current).forEach(clearTimeout); }, []);

  useEffect(() => {
    const handler = (e) => { if (copiarRef.current && !copiarRef.current.contains(e.target)) setShowCopiarPicker(false); };
    if (showCopiarPicker) { document.addEventListener("mousedown", handler); return () => document.removeEventListener("mousedown", handler); }
  }, [showCopiarPicker]);

  const abrirPickerCopiar = async () => {
    if (!containerAtual?.id) return;
    const [{ data: projetos }, { data: setores }] = await Promise.all([
      supabase.from("projects").select("id, name").eq("user_id", containerAtual.id),
      supabase.from("setores").select("id, name").eq("user_id", containerAtual.id),
    ]);
    setDestinosDisponiveis({ projetos: projetos || [], setores: setores || [] });
    setShowCopiarPicker(true);
  };

  const copiarParaDestino = async (destino, tipo) => {
    setCopiando(true);
    try {
      const idField = tipo === "project" ? "project_id" : "setor_id";
      const { data: pilhas } = await supabase.from("pilhas")
        .select("id, title, ordem")
        .eq(idField, destino.id)
        .neq("title", "Recebidos")
        .or("arquivada.is.null,arquivada.eq.false")
        .order("ordem", { ascending: true })
        .limit(1);
      const pilhaDestino = pilhas?.[0];
      if (!pilhaDestino) { alert("Nenhuma pilha disponível no destino."); setCopiando(false); return; }

      const { data: notaOriginal } = await supabase.from("notas").select("*").eq("id", notaId).single();
      if (!notaOriginal) throw new Error("Nota original não encontrada.");

      const { data: notaNova, error: errNota } = await supabase.from("notas").insert({
        nome: notaOriginal.nome, tipo: notaOriginal.tipo, pilha_id: pilhaDestino.id,
        descricao: notaOriginal.descricao, created_by: usuarioId,
        concluida: false, progresso: 0, ordem: 0,
      }).select().single();
      if (errNota) throw errNota;

      for (const meta of metas) {
        const { data: metaNova, error: errMeta } = await supabase.from("metas").insert({
          nota_id: notaNova.id, descricao: meta.descricao, data_entrega: meta.data_entrega,
          concluido: false, concluido_em: null, comentario: "", ordem: meta.ordem, cor: meta.cor || "#ffffff",
        }).select().single();
        if (errMeta) continue;
        if (meta.responsaveis?.length) {
          await supabase.from("metas_responsaveis").insert(
            meta.responsaveis.map(r => ({ meta_id: metaNova.id, usuario_id: r.usuario_id || null, nome_externo: r.nome_externo || null }))
          );
        }
      }

      setShowCopiarPicker(false);
      setCopiando(false);
      if (typeof onNotaCopiada === "function") onNotaCopiada(notaNova, { entityId: destino.id, entityType: tipo });
    } catch (err) {
      console.error("Erro ao copiar:", err);
      alert("Erro ao copiar para o destino selecionado.");
      setCopiando(false);
    }
  };

  useEffect(() => {
    if (!usuarioId) return;
    supabase.from("profiles").select("nome").eq("id", usuarioId).single()
      .then(({ data }) => { if (data?.nome) setMeuNome(data.nome); });
  }, [usuarioId]);

  const sendNotification = useCallback(async (recipientUserId, message, type) => {
    if (!recipientUserId || !usuarioId) return;
    try {
      await supabase.from("notificacoes").insert([{
        user_id: recipientUserId,
        remetente_id: usuarioId,
        mensagem: message,
        tipo: type,
        lido: false,
        created_at: new Date().toISOString(),
        nota_id: notaId || null,
        projeto_id: entityType === "project" ? (projectId || null) : null,
        setor_id: entityType === "setor" ? (setorId || null) : null,
        container_id: containerAtual?.id || null,
      }]);
    } catch (err) {
      console.error("Erro ao enviar notificação:", err);
    }
  }, [usuarioId, notaId, projectId, setorId, entityType, containerAtual]);

  // Descrição da nota
  useEffect(() => {
    if (!notaId) return;
    supabase.from("notas").select("descricao").eq("id", notaId).single()
      .then(({ data }) => {
        if (!data?.descricao) return;
        try {
          const parsed = typeof data.descricao === "string" ? JSON.parse(data.descricao) : data.descricao;
          if (parsed?.text !== undefined) setDescricaoData(parsed);
          else setDescricaoData({ text: String(data.descricao), fontSize: 14, color: "#374151", align: "left" });
        } catch {
          setDescricaoData({ text: String(data.descricao), fontSize: 14, color: "#374151", align: "left" });
        }
      });
  }, [notaId]);

  const salvarDescricao = useCallback(async (dados) => {
    if (!notaId) return;
    setSalvandoDesc(true);
    await supabase.from("notas").update({ descricao: JSON.stringify(dados) }).eq("id", notaId);
    setSalvandoDesc(false);
  }, [notaId]);

  const handleDescricaoChange = (novosDados) => {
    setDescricaoData(novosDados);
    clearTimeout(descTimerRef.current);
    descTimerRef.current = setTimeout(() => salvarDescricao(novosDados), 500);
  };

  const carregarComentarios = useCallback(async (metaId) => {
    if (!metaId) return [];
    const { data, error } = await supabase.from("metas_comentarios").select("*").eq("meta_id", metaId).order("created_at", { ascending: true });
    if (error || !data?.length) return [];
    const userIds = [...new Set(data.map(c => c.usuario_id))];
    const { data: profiles } = await supabase.from("profiles").select("id, nome").in("id", userIds);
    const profilesMap = (profiles || []).reduce((acc, p) => { acc[p.id] = p.nome; return acc; }, {});
    return data.map(c => ({ ...c, profiles: { nome: profilesMap[c.usuario_id] || "Usuário" } }));
  }, []);

  const carregarMetas = useCallback(async () => {
    if (!notaId) return setMetas([]);
    const { data: metasData, error } = await supabase
      .from("metas")
      .select(`*, metas_responsaveis(id, usuario_id, nome_externo)`)
      .eq("nota_id", notaId)
      .order("ordem", { ascending: true })
      .order("criado_em", { ascending: true });

    if (error) { console.error("Erro ao carregar metas:", error); return setMetas([]); }

    const enriquecidas = metasData.map(m => {
      const responsaveis = (m.metas_responsaveis || []).map(r => ({
        id: r.id, usuario_id: r.usuario_id, nome_externo: r.nome_externo,
        nome_exibicao: r.usuario_id ? "" : r.nome_externo, avatar_url: null,
      }));
      delete m.metas_responsaveis;
      return { ...m, responsaveis };
    });

    const idsInternos = enriquecidas.flatMap(m => m.responsaveis).filter(r => r.usuario_id).map(r => r.usuario_id);
    if (idsInternos.length > 0) {
      const { data: profiles } = await supabase.from("profiles").select("id, nome, avatar_url").in("id", idsInternos);
      const map = {};
      profiles?.forEach(p => { map[p.id] = p; });
      enriquecidas.forEach(m => m.responsaveis.forEach(r => {
        if (r.usuario_id && map[r.usuario_id]) {
          r.nome_exibicao = map[r.usuario_id].nome || "Usuário";
          r.avatar_url    = map[r.usuario_id].avatar_url || null;
        }
      }));
    }

    const metaIds = enriquecidas.map(m => m.id);
    if (metaIds.length) {
      const { data: vinculos } = await supabase
        .from("metas_notas_vinculadas")
        .select("id, meta_id, nota_id, notas(id, nome, tipo)")
        .in("meta_id", metaIds);
      const mapaV = {};
      (vinculos || []).forEach(v => {
        (mapaV[v.meta_id] ||= []).push({ vinculoId: v.id, notaId: v.nota_id, nome: v.notas?.nome || "Tarefa vinculada", tipo: v.notas?.tipo || null });
      });
      enriquecidas.forEach(m => { m.tarefasVinculadas = mapaV[m.id] || []; });

      const { data: anexosData } = await supabase.from("metas_anexos").select("*").in("meta_id", metaIds);
      const mapaA = {};
      (anexosData || []).forEach(a => { (mapaA[a.meta_id] ||= []).push(a); });
      setAnexosPorMeta(mapaA);
    } else {
      setAnexosPorMeta({});
    }

    setMetas(enriquecidas);

    const comentariosMap = {};
    for (const m of enriquecidas) comentariosMap[m.id] = await carregarComentarios(m.id);
    setComentariosMeta(comentariosMap);
  }, [notaId, carregarComentarios]);

  useEffect(() => { carregarMetas(); }, [carregarMetas]);

  // ── LISTA: carregar data, comentários e anexos ──
  useEffect(() => {
    if (!notaId) return;
    (async () => {
      const { data: nota } = await supabase.from("notas").select("data_entrega").eq("id", notaId).single();
      setDataEntregaLista(nota?.data_entrega || "");

      const { data: coms } = await supabase.from("metas_lista_comentarios")
        .select("*").eq("nota_id", notaId).order("created_at", { ascending: true });
      if (coms?.length) {
        const ids = [...new Set(coms.map(c => c.usuario_id))];
        const { data: profs } = await supabase.from("profiles").select("id, nome").in("id", ids);
        const mapa = Object.fromEntries((profs || []).map(p => [p.id, p.nome]));
        setComentariosLista(coms.map(c => ({ ...c, profiles: { nome: mapa[c.usuario_id] || "Usuário" } })));
      } else setComentariosLista([]);

      const { data: anexos } = await supabase.from("metas_lista_anexos")
        .select("*").eq("nota_id", notaId).order("created_at", { ascending: true });
      setAnexosLista(anexos || []);
    })();
  }, [notaId]);

  // Fecha popover de comentários ao clicar fora
  useEffect(() => {
    if (!showComentariosLista) return;
    const handler = (e) => {
      if (comentariosListaRef.current && !comentariosListaRef.current.contains(e.target)) setShowComentariosLista(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showComentariosLista]);

  const atualizarDataEntregaLista = async (valor) => {
    setDataEntregaLista(valor);
    await supabase.from("notas").update({ data_entrega: valor || null }).eq("id", notaId);
  };

  const enviarComentarioLista = async () => {
    const texto = comentarioListaTemp.trim();
    if (!texto || !usuarioId || enviandoComentarioLista) return;
    setEnviandoComentarioLista(true);
    const tempId = `temp-${Date.now()}`;
    setComentariosLista(prev => [...prev, { id: tempId, nota_id: notaId, usuario_id: usuarioId, comentario: texto, created_at: new Date().toISOString(), profiles: { nome: meuNome } }]);
    setComentarioListaTemp("");
    try {
      const { data, error } = await supabase.from("metas_lista_comentarios")
        .insert({ nota_id: notaId, usuario_id: usuarioId, comentario: texto }).select().single();
      if (error) throw error;
      setComentariosLista(prev => prev.map(c => c.id === tempId ? { ...data, profiles: { nome: meuNome } } : c));
    } catch (err) {
      console.error(err);
      setComentariosLista(prev => prev.filter(c => c.id !== tempId));
      setComentarioListaTemp(texto);
      alert("Erro ao enviar comentário.");
    } finally { setEnviandoComentarioLista(false); }
  };

  const removerComentarioLista = async (c) => {
    if (String(c.id).startsWith("temp-") || c.usuario_id !== usuarioId) return;
    if (!window.confirm("Excluir este comentário?")) return;
    await supabase.from("metas_lista_comentarios").delete().eq("id", c.id);
    setComentariosLista(prev => prev.filter(x => x.id !== c.id));
  };

  const handleUploadAnexoLista = async (files) => {
    if (!files?.length || !notaId) return;
    setUploadandoAnexoLista(true);
    try {
      for (const file of Array.from(files)) {
        const ext  = file.name.split(".").pop();
        const path = `metas-lista/${notaId}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("ata-anexos").upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from("ata-anexos").getPublicUrl(path);
        const { data: anexo, error: dbErr } = await supabase.from("metas_lista_anexos").insert({
          nota_id: notaId, nome_arquivo: file.name, url: urlData.publicUrl,
          storage_path: path, tamanho: file.size, tipo_mime: file.type, enviado_por: usuarioId,
        }).select().single();
        if (dbErr) throw dbErr;
        setAnexosLista(prev => [...prev, anexo]);
      }
    } catch (err) { console.error(err); alert("Erro ao enviar arquivo."); }
    finally { setUploadandoAnexoLista(false); }
  };

  const handleRemoverAnexoLista = async (anexo) => {
    if (!window.confirm(`Remover "${anexo.nome_arquivo}"?`)) return;
    try {
      await supabase.storage.from("ata-anexos").remove([anexo.storage_path]);
      await supabase.from("metas_lista_anexos").delete().eq("id", anexo.id);
      setAnexosLista(prev => prev.filter(a => a.id !== anexo.id));
    } catch (err) { console.error(err); alert("Erro ao remover arquivo."); }
  };

  const todosResponsaveis = useMemo(() => {
    const seen = new Set(); const lista = [];
    for (const m of metas) for (const r of m.responsaveis) {
      const key = r.usuario_id || r.nome_externo;
      if (key && !seen.has(key)) { seen.add(key); lista.push(r); }
    }
    return lista;
  }, [metas]);

  // ── Respondidas por mim (via comentários) ──
  const respondidosPorMim = useMemo(() => {
    const s = new Set();
    Object.entries(comentariosMeta).forEach(([metaId, lista]) => {
      if ((lista || []).some(c => c.usuario_id === usuarioId)) s.add(metaId);
    });
    return s;
  }, [comentariosMeta, usuarioId]);

  // ── Filtro ──
  const metasFiltradas = useMemo(() => {
    let lista = metas;
    if (filtroResponsavel) {
      lista = lista.filter(m => m.responsaveis.some(r => (r.usuario_id && r.usuario_id === filtroResponsavel) || (!r.usuario_id && r.nome_externo === filtroResponsavel)));
    }
    if (arquivarConcluidos) lista = lista.filter(m => !m.concluido || concluidosRecentes[m.id]);
    if (soEmAberto)         lista = lista.filter(m => (!m.concluido && !respondidosPorMim.has(m.id)) || concluidosRecentes[m.id]);
    if (soSemResp)          lista = lista.filter(m => m.responsaveis.length === 0);

    const termo = buscaMetas.trim().toLowerCase();
    if (termo) lista = lista.filter(m => m.descricao?.toLowerCase().includes(termo));
    return lista;
  }, [metas, filtroResponsavel, arquivarConcluidos, soEmAberto, soSemResp, buscaMetas, respondidosPorMim, concluidosRecentes]);

  // ── Ordenação ──
  const listaOrdenada = useMemo(() => {
    const base = [...metasFiltradas];
    const nomeResp = m => (m.responsaveis[0]?.nome_exibicao || m.responsaveis[0]?.nome_externo || "").toLowerCase();
    const dataEnt  = m => m.data_entrega || "";
    const cmp = (a, b) => { if (!a && !b) return 0; if (!a) return 1; if (!b) return -1; return a.localeCompare(b); };
    if (ordenacao === 'recentes')    return base.reverse();
    if (ordenacao === 'responsavel') return base.sort((a, b) => cmp(nomeResp(a), nomeResp(b)));
    if (ordenacao === 'data')        return base.sort((a, b) => cmp(dataEnt(a), dataEnt(b)));
    return base;
  }, [metasFiltradas, ordenacao]);

  const adicionarMeta = async () => {
    if (!novoMetaTexto.trim()) return;
    try {
      const maxOrdem = metas.reduce((max, m) => Math.max(max, m.ordem ?? 0), -1);
      const { error, data } = await supabase.from("metas").insert({
        nota_id: notaId, descricao: novoMetaTexto.trim(),
        data_entrega: null, concluido: false, concluido_em: null,
        comentario: "", ordem: maxOrdem + 1, cor: "#ffffff",
      }).select().single();
      if (error) throw error;
      setMetas(prev => [...prev, { ...data, responsaveis: [], tarefasVinculadas: [] }]);
      setNovoMetaTexto("");
    } catch (err) {
      console.error("Erro ao adicionar meta:", err);
      alert("Erro ao adicionar meta.");
    }
  };

  const handleDragEnd = async (result) => {
    if (!arrastavel) return;
    if (!result.destination) return;
    const temFiltro = filtroResponsavel || arquivarConcluidos || soSemResp || soEmAberto || buscaMetas.trim();
    const listaBase = temFiltro ? metasFiltradas : metas;
    const n = listaBase.length;
    const sourceIdx = ordemDesc ? (n - 1 - result.source.index) : result.source.index;
    const destIdx   = ordemDesc ? (n - 1 - result.destination.index) : result.destination.index;
    if (sourceIdx === destIdx) return;
    const reordenada = Array.from(listaBase);
    const [moved] = reordenada.splice(sourceIdx, 1);
    reordenada.splice(destIdx, 0, moved);

    let novaLista;
    if (temFiltro) {
      novaLista = [...metas];
      let ri = 0;
      for (let idx = 0; idx < novaLista.length; idx++) {
        if (metasFiltradas.some(f => f.id === novaLista[idx].id)) novaLista[idx] = reordenada[ri++];
      }
    } else { novaLista = reordenada; }

    setMetas(novaLista);
    for (let i = 0; i < novaLista.length; i++) {
      if (novaLista[i].id) await supabase.from("metas").update({ ordem: i }).eq("id", novaLista[i].id);
    }
  };

  const atualizarCor = async (id, cor) => {
    setMetas(prev => prev.map(m => m.id === id ? { ...m, cor } : m));
    setColorPickerMetaId(null);
    await supabase.from("metas").update({ cor }).eq("id", id);
  };

  const iniciarEdicaoMeta = (meta) => {
    setEditandoMetaId(meta.id);
    setTextoEdicaoMeta(meta.descricao || "");
  };

  const salvarEdicaoMeta = async (meta) => {
    const novo = textoEdicaoMeta.trim();
    setEditandoMetaId(null);
    if (!novo || novo === meta.descricao) return;
    setMetas(prev => prev.map(m => m.id === meta.id ? { ...m, descricao: novo } : m));
    await supabase.from("metas").update({ descricao: novo }).eq("id", meta.id);
  };

  const cancelarEdicaoMeta = () => setEditandoMetaId(null);

  const handleResponsavelInputChange = (e, metaId) => {
    const valor = e.target.value;
    setInputResponsavel(prev => ({ ...prev, [metaId]: valor }));

    if (valor.startsWith("@") && valor.length > 1) {
      const termo = valor.slice(1).toLowerCase();
      const cid   = containerAtual?.id;

      if (cid) {
        supabase.from("convites").select("user_id")
          .eq("container_id", cid).eq("status", "aceito")
          .then(async ({ data: convites, error: convErr }) => {
            if (convErr || !convites?.length) return setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] }));
            const userIds = convites.map(c => c.user_id).filter(Boolean);
            if (!userIds.length) return setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] }));
            const { data: profiles } = await supabase.from("profiles").select("id, nome, nickname, avatar_url").in("id", userIds);
            const filtrados = (profiles || []).filter(p => p.nickname?.toLowerCase().includes(termo) || p.nome?.toLowerCase().includes(termo));
            const seen = new Set();
            const unicos = filtrados.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; });
            setSugestoesResponsavel(prev => ({ ...prev, [metaId]: unicos.slice(0, 10) }));
          }).catch(() => setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] })));
      } else if (projectId || setorId) {
        const campo = projectId ? "project_id" : "setor_id";
        const tabela = projectId ? "project_members" : "setor_members";
        const idAlvo = projectId || setorId;
        supabase.from(tabela).select("user_id").eq(campo, idAlvo)
          .then(async ({ data: membros }) => {
            if (!membros?.length) return setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] }));
            const userIds = membros.map(m => m.user_id);
            const { data: profiles } = await supabase.from("profiles").select("id, nome, nickname, avatar_url").in("id", userIds);
            const filtrados = (profiles || []).filter(p => p.nome?.toLowerCase().includes(termo) || p.nickname?.toLowerCase().includes(termo));
            setSugestoesResponsavel(prev => ({ ...prev, [metaId]: filtrados.slice(0, 10) }));
          });
      }
    } else {
      setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] }));
    }
  };

  const adicionarResponsavelInterno = async (usuario, metaId) => {
    const meta = metas.find(m => m.id === metaId);
    if (!meta || meta.concluido || meta.responsaveis.some(r => r.usuario_id === usuario.id)) return;

    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const novoResp = {
      id: tempId,
      usuario_id: usuario.id,
      nome_externo: null,
      nome_exibicao: usuario.nome || usuario.nickname || "Usuário",
      avatar_url: usuario.avatar_url || null,
    };

    setMetas(prev => prev.map(m => m.id === metaId
      ? { ...m, responsaveis: [...m.responsaveis, novoResp] } : m));
    setInputResponsavel(prev => ({ ...prev, [metaId]: "" }));
    setSugestoesResponsavel(prev => ({ ...prev, [metaId]: [] }));
    setEditingResponsavelId(null);

    try {
      const { data, error } = await supabase.from("metas_responsaveis")
        .insert({ meta_id: metaId, usuario_id: usuario.id, nome_externo: null })
        .select().single();
      if (error) throw error;

      setMetas(prev => prev.map(m => m.id === metaId
        ? { ...m, responsaveis: m.responsaveis.map(r => r.id === tempId ? { ...r, id: data.id } : r) }
        : m));

      await sendNotification(usuario.id, `Você foi designado responsável pela meta: ${meta.descricao}`, "meta_responsavel");
    } catch (err) {
      console.error("Erro ao adicionar responsável interno:", err);
      setMetas(prev => prev.map(m => m.id === metaId
        ? { ...m, responsaveis: m.responsaveis.filter(r => r.id !== tempId) } : m));
      alert("Erro ao adicionar responsável.");
    }
  };

  const adicionarResponsavelExterno = async (nome, metaId) => {
    if (!nome.trim()) return;
    const meta = metas.find(m => m.id === metaId);
    if (!meta || meta.concluido || meta.responsaveis.some(r => r.nome_externo === nome && !r.usuario_id)) return;
    try {
      const { data } = await supabase.from("metas_responsaveis")
        .insert({ meta_id: metaId, nome_externo: nome.trim(), usuario_id: null }).select().single();
      if (!data) return;
      setMetas(prev => prev.map(m => m.id === metaId
        ? { ...m, responsaveis: [...m.responsaveis, { id: data.id, nome_externo: nome.trim(), usuario_id: null, nome_exibicao: nome.trim(), avatar_url: null }] }
        : m));
      setInputResponsavel(prev => ({ ...prev, [metaId]: "" }));
      setEditingResponsavelId(null);
    } catch (err) { console.error("Erro ao adicionar responsável externo:", err); }
  };

  const removerResponsavel = async (responsavelId, metaId) => {
    setMetas(prev => prev.map(m => m.id === metaId ? { ...m, responsaveis: m.responsaveis.filter(r => r.id !== responsavelId) } : m));
    await supabase.from("metas_responsaveis").delete().eq("id", responsavelId);
  };

  const toggleConclusao = async (id, concluidoAtual) => {
    const novoValor = !concluidoAtual;
    setMetas(prev => prev.map(m => m.id === id ? { ...m, concluido: novoValor } : m));

    if (novoValor && (soEmAberto || arquivarConcluidos)) {
      setConcluidosRecentes(prev => ({ ...prev, [id]: true }));
      clearTimeout(timeoutsConclusaoRef.current[id]);
      timeoutsConclusaoRef.current[id] = setTimeout(() => {
        setConcluidosRecentes(prev => { const { [id]: _, ...resto } = prev; return resto; });
        delete timeoutsConclusaoRef.current[id];
      }, 60000);
    }

    await supabase.from("metas").update({
      concluido: novoValor, concluido_em: novoValor ? new Date().toISOString() : null,
    }).eq("id", id);
  };

  const atualizarDataEntrega = async (id, data) => {
    setMetas(prev => prev.map(m => m.id === id ? { ...m, data_entrega: data } : m));
    await supabase.from("metas").update({ data_entrega: data || null }).eq("id", id);
  };

  const removerMeta = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta meta?")) return;
    setMetas(prev => prev.filter(m => m.id !== id));
    await supabase.from("metas").delete().eq("id", id);
  };

  const enviarComentario = async (metaId) => {
    const comentario = comentarioTemp[metaId]?.trim();
    if (!comentario || !usuarioId) return;
    if (enviandoComentario[metaId]) return;
    setEnviandoComentario(prev => ({ ...prev, [metaId]: true }));
    const tempId = `temp-${Date.now()}`;
    setComentariosMeta(prev => ({ ...prev, [metaId]: [...(prev[metaId] || []), { id: tempId, meta_id: metaId, usuario_id: usuarioId, comentario, created_at: new Date().toISOString(), profiles: { nome: meuNome } }] }));
    setComentarioTemp(prev => ({ ...prev, [metaId]: "" }));
    try {
      const { data, error } = await supabase.from("metas_comentarios").insert({ meta_id: metaId, usuario_id: usuarioId, comentario }).select().single();
      if (error) throw error;
      setComentariosMeta(prev => ({ ...prev, [metaId]: prev[metaId].map(c => c.id === tempId ? { ...data, profiles: { nome: meuNome } } : c) }));
    } catch (err) {
      console.error("Erro ao enviar comentário:", err);
      setComentariosMeta(prev => ({ ...prev, [metaId]: prev[metaId].filter(c => c.id !== tempId) }));
      setComentarioTemp(prev => ({ ...prev, [metaId]: comentario }));
      alert("Erro ao enviar comentário.");
    } finally { setEnviandoComentario(prev => ({ ...prev, [metaId]: false })); }
  };

  const removerComentario = async (comentario, metaId) => {
    if (!comentario?.id || String(comentario.id).startsWith('temp-')) return;
    if (comentario.usuario_id !== usuarioId) return;
    if (!window.confirm("Excluir este comentário?")) return;
    try {
      await supabase.from("metas_comentarios").delete().eq("id", comentario.id);
      setComentariosMeta(prev => ({ ...prev, [metaId]: prev[metaId].filter(c => c.id !== comentario.id) }));
    } catch (err) { console.error(err); alert("Erro ao excluir comentário."); }
  };

  // ── Anexos por meta ──
  const handleUploadAnexo = async (files, metaId) => {
    if (!files?.length || !metaId || String(metaId).startsWith('temp')) return;
    setUploadandoAnexo(prev => ({ ...prev, [metaId]: true }));
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop();
        const path = `objetivos/${metaId}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("ata-anexos").upload(path, file, { contentType: file.type, upsert: false });
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from("ata-anexos").getPublicUrl(path);

        const { data: anexo, error: dbError } = await supabase.from("metas_anexos").insert({
          meta_id: metaId, nome_arquivo: file.name, url: urlData.publicUrl,
          storage_path: path, tamanho: file.size, tipo_mime: file.type, enviado_por: usuarioId
        }).select().single();
        if (dbError) throw dbError;

        setAnexosPorMeta(prev => ({ ...prev, [metaId]: [...(prev[metaId] || []), anexo] }));
      }
    } catch (err) { console.error("Erro no upload:", err); alert("Erro ao enviar arquivo."); }
    finally { setUploadandoAnexo(prev => ({ ...prev, [metaId]: false })); }
  };

  const handleRemoverAnexo = async (anexo, metaId) => {
    if (!window.confirm(`Remover "${anexo.nome_arquivo}"?`)) return;
    try {
      await supabase.storage.from("ata-anexos").remove([anexo.storage_path]);
      await supabase.from("metas_anexos").delete().eq("id", anexo.id);
      setAnexosPorMeta(prev => ({ ...prev, [metaId]: (prev[metaId] || []).filter(a => a.id !== anexo.id) }));
    } catch (err) { console.error(err); alert("Erro ao remover arquivo."); }
  };

  // ── Modal de vínculo / criar tarefa ──
  const carregarEntidadesContainer = async () => {
    if (!containerAtual?.id) return [];
    setCarregandoModal(true);
    try {
      const [{ data: projetos }, { data: setores }] = await Promise.all([
        supabase.from("projects").select("id, name, photo_url").eq("user_id", containerAtual.id).eq("ativo", true).order("name", { ascending: true }),
        supabase.from("setores").select("id, name").eq("user_id", containerAtual.id).eq("ativo", true).order("name", { ascending: true }),
      ]);
      const lista = [
        ...(projetos || []).map(p => ({ id: p.id, nome: p.name, tipo: 'projeto', foto: p.photo_url || null })),
        ...(setores  || []).map(s => ({ id: s.id, nome: s.name, tipo: 'setor',   foto: null })),
      ];
      setEntidadesContainer(lista);
      return lista;
    } finally { setCarregandoModal(false); }
  };

  const abrirModalVinculo = (metaId, modo) => {
    setModalVinculo({ metaId, modo });
    setModalView('entidades');
    setEntidadeSel(null);
    setItensModal([]);
    setBuscaModal("");
    if (!entidadesContainer) carregarEntidadesContainer();
  };

  const fecharModalVinculo = () => {
    setModalVinculo(null); setModalView('entidades');
    setEntidadeSel(null); setItensModal([]); setBuscaModal("");
  };

  const selecionarEntidadeModal = async (ent) => {
    setCarregandoModal(true);
    try {
      const campo = ent.tipo === 'projeto' ? 'project_id' : 'setor_id';
      const { data: pilhas, error } = await supabase.from("pilhas").select("id, title, tipo_pilha").eq(campo, ent.id).order("title", { ascending: true });
      if (error) throw error;

      if (modalVinculo.modo === 'tarefa') {
        if (!pilhas?.length) { alert("Nenhuma pilha encontrada neste projeto/setor."); return; }
        setItensModal(pilhas.map(p => ({ id: p.id, nome: p.title || "Pilha sem nome", tipo: p.tipo_pilha || "Pilha" })));
      } else {
        if (!pilhas?.length) setItensModal([]);
        else {
          const pilhasMap = Object.fromEntries(pilhas.map(p => [p.id, p.title || "Pilha sem nome"]));
          const { data: notas, error: errN } = await supabase.from("notas").select("id, nome, tipo, pilha_id")
            .neq("id", notaId).in("pilha_id", pilhas.map(p => p.id)).order("nome", { ascending: true });
          if (errN) throw errN;
          setItensModal((notas || []).map(n => ({ id: n.id, nome: n.nome, tipo: n.tipo, pilhaNome: pilhasMap[n.pilha_id] || "Sem pilha" })));
        }
      }
      setEntidadeSel(ent); setBuscaModal(""); setModalView('itens');
    } catch (err) { console.error(err); alert("Erro ao carregar dados."); }
    finally { setCarregandoModal(false); }
  };

  const linkarNota = async (nota) => {
    const metaId = modalVinculo?.metaId;
    const meta = metas.find(m => m.id === metaId);
    if (!meta) return;
    if (meta.tarefasVinculadas?.some(t => t.notaId === nota.id)) return;
    try {
      const { data: v, error } = await supabase.from("metas_notas_vinculadas")
        .insert({ meta_id: metaId, nota_id: nota.id, criado_por: usuarioId }).select("id").single();
      if (error) throw error;
      setMetas(prev => prev.map(m => m.id === metaId
        ? { ...m, tarefasVinculadas: [...(m.tarefasVinculadas || []), { vinculoId: v.id, notaId: nota.id, nome: nota.nome, tipo: nota.tipo }] } : m));
    } catch (err) { console.error(err); alert("Erro ao vincular tarefa."); }
  };

  const removerLinkNota = async (metaId, vinculoId) => {
    if (!window.confirm("Remover vínculo com esta tarefa?")) return;
    const { error } = await supabase.from("metas_notas_vinculadas").delete().eq("id", vinculoId);
    if (error) { alert("Erro ao remover vínculo."); return; }
    setMetas(prev => prev.map(m => m.id === metaId
      ? { ...m, tarefasVinculadas: m.tarefasVinculadas.filter(t => t.vinculoId !== vinculoId) } : m));
  };

  const criarTarefaNaPilha = async (pilhaId) => {
    const metaId = modalVinculo?.metaId;
    const meta = metas.find(m => m.id === metaId);
    if (!meta) return;
    fecharModalVinculo();
    try {
      const { data: notaAtualDb } = await supabase.from("notas").select("container_id").eq("id", notaId).single();
      const responsaveisNomes = meta.responsaveis.map(r => r.nome_exibicao || "Usuário").join(", ");
      const descricao = `**Criado a partir de meta**\n\n📋 Lista de metas: ${notaNome || "Metas"}\n📂 ${entityType === "setor" ? "Setor" : "Projeto"}: ${projetoNome || ""}\n👤 Responsáveis: ${responsaveisNomes || "Sem responsáveis"}`;

      const { data: novaNota, error } = await supabase.from("notas").insert({
        nome: meta.descricao, pilha_id: pilhaId,
        container_id: notaAtualDb?.container_id || containerAtual?.id || null,
        tipo: "Tarefas", created_by: usuarioId, descricao,
      }).select().single();
      if (error) throw error;

      const respInsert = meta.responsaveis.filter(r => r.usuario_id).map(r => ({ nota_id: novaNota.id, usuario_id: r.usuario_id }));
      if (respInsert.length) await supabase.from("nota_responsaveis").insert(respInsert);
      if (meta.data_entrega) await supabase.from("metas").update({ data_entrega: meta.data_entrega }).eq("id", novaNota.id);

      const { data: v } = await supabase.from("metas_notas_vinculadas")
        .insert({ meta_id: metaId, nota_id: novaNota.id, criado_por: usuarioId }).select("id").single();
      setMetas(prev => prev.map(m => m.id === metaId
        ? { ...m, tarefasVinculadas: [...(m.tarefasVinculadas || []), { vinculoId: v.id, notaId: novaNota.id, nome: meta.descricao, tipo: "Tarefas" }] } : m));
      alert("✅ Tarefa criada!");
    } catch (err) { console.error(err); alert("Erro ao criar tarefa."); }
  };

  const progressoPercent = metas.length
    ? Math.round((metas.filter(m => m.concluido).length / metas.length) * 100) : 0;

  useEffect(() => {
    if (!colorPickerMetaId) return;
    const handler = (e) => {
      if (!e.target.closest('.color-picker-popover') && !e.target.closest('.icone-cor')) setColorPickerMetaId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colorPickerMetaId]);

  return (
    <div className="metas-modal-overlay">
      <div className="metas-card" ref={cardRef}>

        <div className="listagem-card">
          <div className="listagem-header-container">
            <div className="listagem-header-titles">
              <span className="project-name">{projetoNome || "Projeto"}</span>
              <div className="sub-info"><span className="nota-name">{notaNome || "Metas"}</span></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} ref={copiarRef}>
              <button className="listagem-close-btn" onClick={abrirPickerCopiar} title="Copiar para outro projeto/setor" disabled={copiando}>
                <FontAwesomeIcon icon={faCopy} />
              </button>
              {showCopiarPicker && (
                <div className="menu-atas-disponiveis" style={{ top: '100%', right: 0 }}>
                  <div className="menu-atas-header">
                    <span>Copiar para:</span>
                    <button onClick={() => setShowCopiarPicker(false)}>×</button>
                  </div>
                  {destinosDisponiveis.projetos.length === 0 && destinosDisponiveis.setores.length === 0 && (
                    <div className="menu-atas-vazio">Nenhum projeto ou setor disponível</div>
                  )}
                  {destinosDisponiveis.projetos.map(p => (
                    <div key={`p-${p.id}`} className="menu-atas-item" onClick={() => copiarParaDestino(p, "project")}>
                      <div className="menu-atas-nome">{p.name}</div>
                      <div className="menu-atas-pauta">Projeto</div>
                    </div>
                  ))}
                  {destinosDisponiveis.setores.map(s => (
                    <div key={`s-${s.id}`} className="menu-atas-item" onClick={() => copiarParaDestino(s, "setor")}>
                      <div className="menu-atas-nome">{s.name}</div>
                      <div className="menu-atas-pauta">Setor</div>
                    </div>
                  ))}
                </div>
              )}
              {onClose && (
                <button className="listagem-close-btn" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
              )}
            </div>
          </div>
        </div>

        <div className="metas-container-completo">
          <div className="metas-header">
            {salvandoDesc && <span style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>salvando…</span>}
          </div>

          <DescricaoArea descricaoData={descricaoData} onChange={handleDescricaoChange} />

          {/* ── Cabeçalho da lista: data, comentários e anexos ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0 8px', flexWrap: 'wrap' }}>

            {/* Data de entrega da lista */}
            <label className="obj-data-label" title="Data de entrega da lista">
              {dataEntregaLista && <span className="obj-data-texto">{formatarDataParaExibicao(dataEntregaLista)}</span>}
              <FontAwesomeIcon icon={faCalendar} className="obj-data-icon" />
              <input type="date" className="obj-date-trigger" value={dataEntregaLista || ''}
                onChange={e => atualizarDataEntregaLista(e.target.value)} />
            </label>

            {/* Imagem */}
            <label className="obj-icon-btn" title="Anexar imagem">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => { handleUploadAnexoLista(e.target.files); e.target.value = ''; }} />
            </label>

            {/* Arquivo */}
            <label className="obj-icon-btn" title="Anexar arquivo">
              {uploadandoAnexoLista ? <span className="spinner-comentario" /> : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              )}
              <input type="file" multiple style={{ display: 'none' }}
                onChange={e => { handleUploadAnexoLista(e.target.files); e.target.value = ''; }} />
            </label>

            {/* Comentários */}
            <div style={{ position: 'relative' }} ref={comentariosListaRef}>
              <ComentarioIcon
                onClick={() => setShowComentariosLista(v => !v)}
                title={comentariosLista.length ? `Ver comentários (${comentariosLista.length})` : "Comentar"}
                hasComments={comentariosLista.length > 0}
              />
              {showComentariosLista && (
                <div className="comentario-chat-container">
                  <div className="comentario-input-area">
                    <input type="text" className="comentario-input" placeholder="Adicionar comentário..."
                      value={comentarioListaTemp} disabled={enviandoComentarioLista}
                      onChange={e => setComentarioListaTemp(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarComentarioLista(); } }} />
                    <button className="btn-enviar-comentario" onClick={enviarComentarioLista}
                      disabled={!comentarioListaTemp.trim() || enviandoComentarioLista}>
                      {enviandoComentarioLista ? <span className="spinner-comentario" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                    </button>
                  </div>
                  {comentariosLista.length > 0 && (
                    <div className="comentarios-lista">
                      {comentariosLista.map(c => {
                        const isTemp = String(c.id).startsWith('temp-');
                        return (
                          <div key={c.id} className={`comentario-item ${isTemp ? 'comentario-temporario' : ''}`}>
                            <div className="comentario-item-header">
                              <span className="comentario-autor">{c.profiles?.nome || "Usuário"}</span>
                              {c.usuario_id === usuarioId && !isTemp && (
                                <button className="comentario-excluir-btn" onClick={() => removerComentarioLista(c)} title="Excluir comentário">×</button>
                              )}
                            </div>
                            <div className="comentario-texto">{c.comentario}</div>
                            <div className="comentario-data">
                              {isTemp ? <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Enviando...</span>
                                : new Date(c.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Anexos da lista */}
          {anexosLista.length > 0 && (
            <div className="obj-anexos" style={{ marginBottom: 8 }}>
              {anexosLista.filter(ehImagem).length > 0 && (
                <div className="obj-anexos-imagens">
                  {anexosLista.filter(ehImagem).map(a => (
                    <div key={a.id} className="obj-anexo-thumb">
                      <img src={a.url} alt={a.nome_arquivo} loading="lazy" onClick={() => setImagemAberta(a)} />
                      <button className="obj-anexo-thumb-remover" title="Remover" onClick={() => handleRemoverAnexoLista(a)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {anexosLista.filter(a => !ehImagem(a)).map(a => (
                <div key={a.id} className="obj-anexo-item">
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="obj-anexo-nome">📎 {a.nome_arquivo}</a>
                  <button className="obj-anexo-remover" onClick={() => handleRemoverAnexoLista(a)}>×</button>
                </div>
              ))}
            </div>
          )}

          <div className="objetivos-header">
            <div className="objetivos-header-titulo">
              <span>Lista de metas</span>
              <span className="objetivos-contador">({metas.length} · {metas.filter(m => m.concluido).length} concluídas)</span>
            </div>
            <select
              value={ordenacao}
              onChange={e => setOrdenacao(e.target.value)}
              title="Ordenar metas"
              style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid transparent', background: 'transparent', color: '#9ca3af', cursor: 'pointer', outline: 'none', maxWidth: 130 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
            >
              <option value="recentes">Recentes primeiro</option>
              <option value="antigos">Antigos primeiro</option>
              <option value="responsavel">Por responsável</option>
              <option value="data">Por data de entrega</option>
            </select>
          </div>

          <div className="progress-segmentada-wrap">
            <div className="progress-segmentada">
              {metas.map(m => <div key={m.id} className={`progress-segmento ${m.concluido ? 'preenchido' : ''}`} title={m.descricao} />)}
              {metas.length === 0 && <div className="progress-segmento progress-segmento--vazio" />}
            </div>
            <span className="progress-segmentada-percent">{progressoPercent}%</span>
          </div>

          <div className="objetivos-add-form objetivos-add-form--destaque">
            <span className="objetivos-add-icon">＋</span>
            <input
              type="text"
              value={novoMetaTexto}
              onChange={(e) => setNovoMetaTexto(e.target.value)}
              placeholder="Digite uma nova meta e pressione Enter..."
              onKeyDown={(e) => e.key === "Enter" && adicionarMeta()}
              disabled={salvando}
            />
            <button type="button" className="objetivos-add-btn" onClick={adicionarMeta} disabled={!novoMetaTexto.trim() || salvando}>
              Adicionar
            </button>
          </div>

          <div className="objetivos-scroll">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="metas-list">
                {(provided) => (
                  <div className="objetivos-list-inner" ref={provided.innerRef} {...provided.droppableProps}>
                    {listaOrdenada.map((meta, filtroIdx) => {
                      const i = metas.findIndex(m => m.id === meta.id);
                      if (i === -1) return null;
                      const isConcluido   = meta.concluido;
                      const podeDesmarcar = podeDesmarcarConclusao(meta.concluido_em);
                      const corLinha      = meta.cor || "#ffffff";
                      const comentarios   = comentariosMeta[meta.id] || [];
                      const hasComments   = comentarios.length > 0;
                      const editandoEsta  = editandoMetaId === meta.id;

                      return (
                        <Draggable
                          key={String(meta.id)}
                          draggableId={String(meta.id)}
                          index={filtroIdx}
                          isDragDisabled={isConcluido || editandoEsta || !arrastavel}
                        >
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...(!isConcluido && arrastavel ? prov.dragHandleProps : {})}
                              className={`obj-card ${isConcluido ? 'obj-card--concluido' : ''} ${snapshot.isDragging ? 'obj-card--dragging' : ''}`}
                              style={{ ...prov.draggableProps.style, backgroundColor: isConcluido ? undefined : corLinha, opacity: snapshot.isDragging ? 0.85 : 1 }}
                            >
                              <div className="obj-linha1">
                                {!isConcluido && arrastavel && (
                                  <span className="meta-drag-handle" title="Arrastar"><FaGripVertical /></span>
                                )}
                                <input
                                  type="checkbox"
                                  checked={isConcluido}
                                  onChange={() => toggleConclusao(meta.id, isConcluido)}
                                  disabled={isConcluido && !podeDesmarcar}
                                  className="obj-checkbox"
                                />

                                <span className={`obj-texto ${isConcluido ? 'obj-texto--concluido' : ''}`}>
                                  <strong className="obj-num">{i + 1}.</strong>{" "}
                                  {editandoEsta ? (
                                    <input
                                      type="text"
                                      className="obj-texto-input"
                                      autoFocus
                                      value={textoEdicaoMeta}
                                      onChange={e => setTextoEdicaoMeta(e.target.value)}
                                      onBlur={() => salvarEdicaoMeta(meta)}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
                                        else if (e.key === "Escape") { e.preventDefault(); cancelarEdicaoMeta(); }
                                      }}
                                      onFocus={e => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                                    />
                                  ) : (
                                    meta.descricao
                                  )}
                                </span>

                                <div className="obj-acoes-dir">
                                  <label className="obj-icon-btn" title="Anexar imagem" onClick={e => e.stopPropagation()}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                    <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                                      onChange={e => { handleUploadAnexo(e.target.files, meta.id); e.target.value = ''; }} />
                                  </label>

                                  <label className="obj-icon-btn" title="Anexar arquivo" onClick={e => e.stopPropagation()}>
                                    {uploadandoAnexo[meta.id] ? <span className="spinner-comentario" /> : (
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                      </svg>
                                    )}
                                    <input type="file" multiple style={{ display: 'none' }}
                                      onChange={e => { handleUploadAnexo(e.target.files, meta.id); e.target.value = ''; }} />
                                  </label>

                                  {!isConcluido && (
                                    <span className="obj-icon-btn" title="Criar tarefa a partir desta meta"
                                      onClick={e => { e.stopPropagation(); abrirModalVinculo(meta.id, 'tarefa'); }}>
                                      <FontAwesomeIcon icon={faTasks} />
                                    </span>
                                  )}

                                  <LinkNotaIcon
                                    onClick={() => abrirModalVinculo(meta.id, 'link')}
                                    title={meta.tarefasVinculadas?.length > 0 ? "Gerenciar vínculos" : "Vincular tarefas"}
                                    temLink={meta.tarefasVinculadas?.length > 0}
                                  />

                                  <div style={{ position: 'relative' }}>
                                    <ComentarioIcon onClick={() => setEditandoComentario(prev => ({ ...prev, [meta.id]: !prev[meta.id] }))}
                                      title={hasComments ? "Ver comentários" : "Comentar"} hasComments={hasComments} />
                                    {editandoComentario[meta.id] && (
                                      <div className="comentario-chat-container">
                                        <div className="comentario-input-area">
                                          <input type="text" value={comentarioTemp[meta.id] || ""} onChange={e => setComentarioTemp(prev => ({ ...prev, [meta.id]: e.target.value }))}
                                            placeholder="Adicionar comentário..." className="comentario-input" disabled={enviandoComentario[meta.id]}
                                            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !enviandoComentario[meta.id]) { e.preventDefault(); enviarComentario(meta.id); } }} />
                                          <button onClick={() => enviarComentario(meta.id)} className="btn-enviar-comentario" disabled={!comentarioTemp[meta.id]?.trim() || enviandoComentario[meta.id]}>
                                            {enviandoComentario[meta.id] ? <span className="spinner-comentario" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                                          </button>
                                        </div>
                                        {comentarios.length > 0 && (
                                          <div className="comentarios-lista">
                                            {comentarios.map((c, idx) => {
                                              const isTemp = String(c.id).startsWith('temp-');
                                              const souAutor = c.usuario_id === usuarioId;
                                              return (
                                                <div key={`${c.id}-${idx}`} className={`comentario-item ${isTemp ? 'comentario-temporario' : ''}`}>
                                                  <div className="comentario-item-header">
                                                    <span className="comentario-autor">{c.profiles?.nome || "Usuário"}</span>
                                                    {souAutor && !isTemp && (
                                                      <button className="comentario-excluir-btn" onClick={() => removerComentario(c, meta.id)} title="Excluir comentário">×</button>
                                                    )}
                                                  </div>
                                                  <div className="comentario-texto">{c.comentario}</div>
                                                  <div className="comentario-data">
                                                    {isTemp ? <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Enviando...</span>
                                                      : new Date(c.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="obj-linha2">
                                <div className="obj-chips-wrap">
                                  {meta.responsaveis.map(resp => (
                                    <ChipResponsavel key={resp.id} responsavel={resp} onRemove={(r) => removerResponsavel(r.id, meta.id)} disabled={isConcluido} />
                                  ))}
                                  {!isConcluido && (
                                    <span className="obj-add-resp-btn" title="Adicionar responsável"
                                      onClick={(e) => { e.stopPropagation(); setEditingResponsavelId(meta.id); }}>
                                      <FontAwesomeIcon icon={faUserPlus} />
                                    </span>
                                  )}
                                </div>

                                {editingResponsavelId === meta.id && !isConcluido && (
                                  <div className="input-responsavel-flutuante">
                                    <input
                                      type="text" autoFocus placeholder="Nome ou @menção"
                                      value={inputResponsavel[meta.id] || ""}
                                      onChange={(e) => handleResponsavelInputChange(e, meta.id)}
                                      onBlur={() => setTimeout(() => setEditingResponsavelId(null), 200)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          const valor = inputResponsavel[meta.id] || "";
                                          if (!valor.startsWith("@")) adicionarResponsavelExterno(valor, meta.id);
                                        } else if (e.key === "Escape") setEditingResponsavelId(null);
                                      }}
                                      disabled={salvando}
                                    />
                                    {sugestoesResponsavel[meta.id]?.length > 0 && (
                                      <div className="sugestoes-list-flutuante">
                                        {sugestoesResponsavel[meta.id].map(item => (
                                          <div key={item.id} className="sugestao-item" onClick={() => adicionarResponsavelInterno(item, meta.id)}>
                                            @{item.nickname || item.nome}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="obj-acoes-linha2">
                                  {!isConcluido && (
                                    <span className="icone-cor" title="Mudar cor da linha"
                                      style={{ color: corLinha === "#ffffff" ? "#aaa" : corLinha }}
                                      onClick={(e) => { e.stopPropagation(); setColorPickerMetaId(colorPickerMetaId === meta.id ? null : meta.id); }}>
                                      <FontAwesomeIcon icon={faPalette} />
                                    </span>
                                  )}

                                  {!isConcluido && (
                                    <span className="obj-icon-btn" title="Editar meta"
                                      onMouseDown={e => e.preventDefault()}
                                      onClick={e => { e.stopPropagation(); iniciarEdicaoMeta(meta); }}>
                                      <FontAwesomeIcon icon={faPen} />
                                    </span>
                                  )}

                                  {colorPickerMetaId === meta.id && (
                                    <div className="color-picker-popover" onClick={e => e.stopPropagation()}>
                                      {CORES_PREDEFINIDAS.map(cor => (
                                        <div key={cor} className={`color-swatch ${corLinha === cor ? 'color-swatch--ativo' : ''}`}
                                          style={{ backgroundColor: cor }} onClick={() => atualizarCor(meta.id, cor)} />
                                      ))}
                                    </div>
                                  )}

                                  {!isConcluido && (
                                    <label className="obj-data-label">
                                      {meta.data_entrega ? <span className="obj-data-texto">{formatarDataParaExibicao(meta.data_entrega)}</span> : null}
                                      <FontAwesomeIcon icon={faCalendar} className="obj-data-icon" />
                                      <input type="date" className="obj-date-trigger"
                                        value={meta.data_entrega || ''}
                                        onChange={(e) => atualizarDataEntrega(meta.id, e.target.value || null)}
                                        disabled={salvando}
                                      />
                                    </label>
                                  )}

                                  {!isConcluido && (
                                    <span className="obj-btn-excluir" title="Excluir meta" onClick={() => removerMeta(meta.id)}>
                                      <FontAwesomeIcon icon={faTrashCan} />
                                    </span>
                                  )}
                                </div>
                              </div>

                              {meta.tarefasVinculadas?.length > 0 && (
                                <div className="obj-tasks-links">
                                  {meta.tarefasVinculadas.map(t => (
                                    <div key={t.vinculoId} className="obj-task-link">
                                      <FontAwesomeIcon icon={faTasks} onClick={() => onOpenTask && onOpenTask(t.notaId)} />
                                      <span onClick={() => onOpenTask && onOpenTask(t.notaId)}>{t.nome}</span>
                                      <button className="obj-task-link-remover" title="Remover vínculo"
                                        onClick={e => { e.stopPropagation(); removerLinkNota(meta.id, t.vinculoId); }}>×</button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {(anexosPorMeta[meta.id] || []).length > 0 && (
                                <div className="obj-anexos">
                                  {(anexosPorMeta[meta.id] || []).filter(ehImagem).length > 0 && (
                                    <div className="obj-anexos-imagens">
                                      {(anexosPorMeta[meta.id] || []).filter(ehImagem).map(a => (
                                        <div key={a.id} className="obj-anexo-thumb">
                                          <img src={a.url} alt={a.nome_arquivo} loading="lazy"
                                            onClick={e => { e.stopPropagation(); setImagemAberta(a); }} />
                                          <button className="obj-anexo-thumb-remover" title="Remover"
                                            onClick={e => { e.stopPropagation(); handleRemoverAnexo(a, meta.id); }}>×</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {(anexosPorMeta[meta.id] || []).filter(a => !ehImagem(a)).map(a => (
                                    <div key={a.id} className="obj-anexo-item">
                                      <a href={a.url} target="_blank" rel="noopener noreferrer" className="obj-anexo-nome">
                                        📎 {a.nome_arquivo}
                                      </a>
                                      <button className="obj-anexo-remover" onClick={() => handleRemoverAnexo(a, meta.id)}>×</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {(todosResponsaveis.length > 0 || metas.length > 0) && (
            <div className="objetivos-footer-filtro">
              <div className="objetivos-footer-filtro-linha">
                <div className="objetivos-footer-filtro-esquerda">
                  <svg className={`icone-filtro-objetivos ${filtroResponsavel ? 'icone-filtro-ativo' : ''}`}
                    width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" title="Filtrar por responsável">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  {todosResponsaveis.map(resp => {
                    const key = resp.usuario_id || resp.nome_externo;
                    const isAtivo = filtroResponsavel === key;
                    const nomeExibicao = resp.nome_exibicao || resp.nome_externo || "Usuário";
                    return (
                      <div key={key} className={`filtro-avatar-wrapper ${isAtivo ? 'filtro-avatar-ativo' : ''}`}
                        title={`Filtrar por ${nomeExibicao}`}
                        onClick={() => setFiltroResponsavel(prev => prev === key ? null : key)}>
                        {resp.avatar_url ? <img src={resp.avatar_url} alt={nomeExibicao} className="filtro-avatar-img" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} /> : null}
                        <div className="filtro-avatar-iniciais" style={{ display: resp.avatar_url ? 'none' : 'flex' }}>{gerarAbreviacao(nomeExibicao)}</div>
                        {isAtivo && <div className="filtro-avatar-badge" />}
                      </div>
                    );
                  })}
                  {filtroResponsavel && <button className="filtro-limpar-btn" onClick={() => setFiltroResponsavel(null)}>✕</button>}
                </div>

                <div className="filtro-status-wrap" style={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 10, color: '#6b7280' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                    <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={arquivarConcluidos} onChange={e => setArquivarConcluidos(e.target.checked)} />
                    Arquivar concluídas
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                    <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={soSemResp} onChange={e => setSoSemResp(e.target.checked)} />
                    Sem responsáveis
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                    <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={soEmAberto} onChange={e => setSoEmAberto(e.target.checked)} />
                    Em aberto
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalVinculo && (() => {
        const ehTarefa  = modalVinculo.modo === 'tarefa';
        const metaModal = metas.find(m => m.id === modalVinculo.metaId);
        const termo     = buscaModal.trim().toLowerCase();
        const base      = modalView === 'entidades' ? (entidadesContainer || []) : itensModal;
        const lista     = base.filter(x => !termo || x.nome.toLowerCase().includes(termo));
        return (
          <div className="modal-linkar-nota-overlay" onClick={fecharModalVinculo}>
            <div className="modal-linkar-nota-box" onClick={e => e.stopPropagation()}>
              <div className="modal-linkar-nota-header">
                <span>
                  {modalView === 'itens' && (
                    <button className="modal-linkar-nota-voltar" title="Voltar"
                      onClick={() => { setModalView('entidades'); setEntidadeSel(null); setBuscaModal(""); }}>←</button>
                  )}
                  {modalView === 'entidades'
                    ? 'Selecione o projeto/setor'
                    : `${ehTarefa ? 'Pilhas' : 'Tarefas'} em: ${entidadeSel?.nome}`}
                </span>
                <button onClick={fecharModalVinculo}>×</button>
              </div>

              <div className="modal-linkar-nota-busca">
                <input type="text" autoFocus value={buscaModal} onChange={e => setBuscaModal(e.target.value)}
                  placeholder={modalView === 'entidades' ? "Buscar projeto ou setor..." : (ehTarefa ? "Buscar pilha..." : "Buscar tarefa...")} />
                {buscaModal && <button className="modal-linkar-nota-busca-limpar" onClick={() => setBuscaModal("")}>×</button>}
              </div>

              {carregandoModal ? (
                <div className="modal-linkar-nota-loading">Carregando...</div>
              ) : lista.length === 0 ? (
                <div className="modal-linkar-nota-vazio">Nada encontrado</div>
              ) : (
                <div className="modal-linkar-nota-lista">
                  {modalView === 'entidades' ? lista.map(ent => (
                    <button key={`${ent.tipo}-${ent.id}`} className="modal-linkar-nota-item modal-linkar-nota-item--entidade"
                      onClick={() => selecionarEntidadeModal(ent)}>
                      <div className="modal-linkar-nota-avatar">
                        {ent.foto && <img src={ent.foto} alt={ent.nome} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />}
                        <div className="modal-linkar-nota-avatar-fallback" style={{ display: ent.foto ? 'none' : 'flex' }}>{ent.nome.charAt(0).toUpperCase()}</div>
                      </div>
                      <div className="modal-linkar-nota-entidade-info">
                        <div className="modal-linkar-nota-nome">{ent.nome}</div>
                        <div className="modal-linkar-nota-tipo">{ent.tipo === 'projeto' ? 'Projeto' : 'Setor'}</div>
                      </div>
                    </button>
                  )) : lista.map(item => {
                    const jaVinc = !ehTarefa && metaModal?.tarefasVinculadas?.some(t => t.notaId === item.id);
                    return (
                      <button key={item.id}
                        className={`modal-linkar-nota-item ${jaVinc ? 'modal-linkar-nota-item--vinculada' : ''}`}
                        disabled={jaVinc}
                        onClick={() => ehTarefa ? criarTarefaNaPilha(item.id) : linkarNota(item)}>
                        <div className="modal-linkar-nota-nome">{item.nome}</div>
                        {!ehTarefa && <div className="modal-linkar-nota-classificacao">{item.pilhaNome}</div>}
                        <div className="modal-linkar-nota-tipo">{jaVinc ? "✓ Já vinculada" : item.tipo}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {!ehTarefa && metaModal?.tarefasVinculadas?.length > 0 && (
                <div className="modal-linkar-nota-footer">
                  <div className="modal-linkar-nota-footer-titulo">Vinculadas:</div>
                  {metaModal.tarefasVinculadas.map(t => (
                    <div key={t.vinculoId} className="modal-linkar-nota-vinculo-item">
                      <span>{t.nome}</span>
                      <button onClick={() => removerLinkNota(metaModal.id, t.vinculoId)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {imagemAberta && (
        <div className="imagem-lightbox-overlay" onClick={() => setImagemAberta(null)}>
          <div className="imagem-lightbox-topo" onClick={e => e.stopPropagation()}>
            <span>{imagemAberta.nome_arquivo}</span>
            <a href={imagemAberta.url} target="_blank" rel="noopener noreferrer">Abrir em nova aba</a>
            <button onClick={() => setImagemAberta(null)}>×</button>
          </div>
          <img src={imagemAberta.url} alt={imagemAberta.nome_arquivo} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}